import type { NodeItem } from '@octopus-do/octopus-project-core'
import type { HydratedBlock } from '@store/migrationSelectors/blocks'
import { getId } from '@store/utils'
import fuzzysort from 'fuzzysort'
import { useMemo } from 'react'

import type { Comment, StickyNote } from '@/types/models'
import useBlocks from '../selectors/useBlocks'
import useNotes from '../selectors/useNotes'
import usePages from '../selectors/usePages'

export enum SearchEntity {
  BLOCK = 'BLOCK',
  BLOCK_NOTE = 'BLOCK_NOTE',
  PAGE = 'PAGE',
  NOTE = 'NOTE',
  COMMENT = 'COMMENT',
}

export type SearchResult = {
  [SearchEntity.BLOCK]: Record<string, HydratedBlock>
  [SearchEntity.BLOCK_NOTE]: Record<string, HydratedBlock>
  [SearchEntity.PAGE]: Record<string, NodeItem>
  [SearchEntity.NOTE]: Record<string, StickyNote>
  [SearchEntity.COMMENT]: Record<string, Comment>
}

const THRESHOLD = -1000

const mapByRandomId = <T>(list: T[]): Record<string, T> =>
  list.reduce(
    (acc, el) => ({
      ...acc,
      [getId()]: el,
    }),
    {}
  )

const useSearchBlocks = (query: string): Record<string, HydratedBlock> => {
  const blocks = useBlocks()

  return useMemo(
    () =>
      mapByRandomId(
        [
          ...fuzzysort.go(query, blocks, {
            key: 'title',
            threshold: THRESHOLD,
          }),
        ].map((el) => el.obj)
      ),
    [query]
  )
}

const useSearchBlockNotes = (query: string): Record<string, HydratedBlock> => {
  const blocks = useBlocks()

  return useMemo(
    () =>
      mapByRandomId(
        [
          ...fuzzysort.go(query, blocks, {
            key: 'content',
            threshold: THRESHOLD,
          }),
        ].map((el) => el.obj)
      ),
    [query]
  )
}

const useSearchPages = (query: string): Record<string, NodeItem> => {
  const pages = Object.values(usePages())

  return useMemo(
    () =>
      mapByRandomId(
        [
          ...fuzzysort.go(query, pages, { key: 'title', threshold: THRESHOLD }),
        ].map((el) => el.obj)
      ),
    [query]
  )
}

const useSearchNotes = (query: string): Record<string, StickyNote> => {
  const notes = useNotes()

  return useMemo(
    () =>
      mapByRandomId(
        [
          ...fuzzysort.go(query, notes, {
            key: 'content',
            threshold: THRESHOLD,
          }),
        ].map((el) => el.obj)
      ),
    [query]
  )
}

const EMPTY = {}

export const useSearch = (
  query: string,
  searchEntities?: SearchEntity[]
): SearchResult => {
  const searchBlocks =
    !searchEntities || searchEntities.includes(SearchEntity.BLOCK)
      ? useSearchBlocks(query)
      : EMPTY

  const searchBlockNotes =
    !searchEntities || searchEntities.includes(SearchEntity.BLOCK_NOTE)
      ? useSearchBlockNotes(query)
      : EMPTY

  const searchPages =
    !searchEntities || searchEntities.includes(SearchEntity.PAGE)
      ? useSearchPages(query)
      : EMPTY

  const searchNotes =
    !searchEntities || searchEntities.includes(SearchEntity.NOTE)
      ? useSearchNotes(query)
      : EMPTY

  const searchComments =
    !searchEntities || searchEntities.includes(SearchEntity.COMMENT)
      ? []
      : EMPTY

  return useMemo(
    () => ({
      [SearchEntity.BLOCK]: searchBlocks,
      [SearchEntity.BLOCK_NOTE]: searchBlockNotes,
      [SearchEntity.PAGE]: searchPages,
      [SearchEntity.NOTE]: searchNotes,
      [SearchEntity.COMMENT]: searchComments,
    }),
    [searchBlocks, searchBlockNotes, searchPages, searchNotes, searchComments]
  )
}
